import { addDays, addHours, addMinutes, addMonths, addYears, format, isAfter, isBefore, isEqual, isValid, parse } from 'date-fns';
import { MAX_DATE, MIN_DATE } from './constants.js';
import { dateAfterMaxAllowed, dateBeforeMinAllowed, dateFormatNotValid, dateNotValid, dateRangeNotValid, rangeIsNotBetweenAllowedMinMax, timeFormatNotValid, timeRangeNotValid, unableParseDateRange, unableParseTimeRange } from './messages.js';
const timeRangeRegex = /\s\s*\([^(]*?\)\s*$/g;
const regexToRemove = /\s\s*\([^(]*?\).*/g;
export function isTimeValidWithFormat(timeString, timeFormat) {
    const parsedTime = parse(timeString, timeFormat, new Date());
    return isValid(parsedTime) && format(parsedTime, timeFormat) === timeString;
}
function isDateValidWithFormat(dateString, dateFormat) {
    const parsedDate = parse(dateString, dateFormat, new Date());
    return isValid(parsedDate) && format(parsedDate, dateFormat) === dateString;
}
function extractTimeRange(dateTimeString, joinString) {
    const timeRangeExtractArray = dateTimeString.match(timeRangeRegex);
    return timeRangeExtractArray?.[0]
        .trim()
        .replace('(', '')
        .replace(')', '')
        .split(` ${joinString} `)
        .map((value) => value.trim());
}
function extractDateRange(dateTimeString, joinString) {
    return dateTimeString
        .replace(regexToRemove, '')
        .trim()
        .split(` ${joinString} `)
        .map((value) => value.trim());
}
export function getValidationAndExtractTimeRange(dateTimeString, joinString, timeFormat) {
    const timesInString = extractTimeRange(dateTimeString, joinString);
    if (!timesInString || timesInString.length !== 2 || !timesInString[0] || !timesInString[1]) {
        return { isValid: false, error: unableParseTimeRange };
    }
    if (!(isTimeValidWithFormat(timesInString[0], timeFormat) && isTimeValidWithFormat(timesInString[1], timeFormat))) {
        return { isValid: false, error: timeFormatNotValid };
    }
    return { isValid: true, startTime: timesInString[0], endTime: timesInString[1] };
}
export function getValidationAndExtractDateRange(dateTimeString, joinString, dateFormat) {
    const datesInString = extractDateRange(dateTimeString, joinString);
    if (!datesInString || datesInString.length !== 2 || !datesInString[0] || !datesInString[1]) {
        return { isValid: false, error: unableParseDateRange };
    }
    if (!(isDateValidWithFormat(datesInString[0], dateFormat) && isDateValidWithFormat(datesInString[1], dateFormat))) {
        return { isValid: false, error: dateFormatNotValid };
    }
    return { isValid: true, startDate: datesInString[0], endDate: datesInString[1] };
}
function validateDateMinMax(date, max, min) {
    const isDateGrtEqMin = isAfter(date, min) || isEqual(date, min);
    const isDateLessEqMax = isBefore(date, max) || isEqual(date, max);
    return isDateGrtEqMin && isDateLessEqMax;
}
function validateDateRangeMinMax(startDate, endDate, max, min) {
    return validateDateMinMax(startDate, max, min) && validateDateMinMax(endDate, max, min);
}
export const dateSelectedValidator = (dateInString, max, min, dateFormat) => {
    if (!isDateValidWithFormat(dateInString, dateFormat)) {
        throw dateNotValid;
    }
    if (isBefore(new Date(dateInString), min)) {
        throw dateBeforeMinAllowed;
    }
    if (isAfter(new Date(dateInString), max)) {
        throw dateAfterMaxAllowed;
    }
};
export const dateTimeRangeValidator = (dateTimeString, max = MAX_DATE, min = MIN_DATE, joinString = 'to', dateFormat = 'MM/dd/yyyy', timeFormat = 'HH:mm:ss') => {
    const dateRangeResult = getValidationAndExtractDateRange(dateTimeString, joinString, dateFormat);
    if (!dateRangeResult.isValid) {
        throw dateRangeResult.error;
    }
    const timeRangeResult = getValidationAndExtractTimeRange(dateTimeString, joinString, timeFormat);
    if (!timeRangeResult.isValid) {
        throw timeRangeResult.error;
    }
    const startDate = new Date(`${dateRangeResult.startDate}`);
    const endDate = new Date(`${dateRangeResult.endDate}`);
    if (!(isAfter(endDate, startDate) || isEqual(endDate, startDate))) {
        throw dateRangeNotValid;
    }
    const startDateTime = new Date(`${dateRangeResult.startDate} ${timeRangeResult.startTime}`);
    const endDateTime = new Date(`${dateRangeResult.endDate} ${timeRangeResult.endTime}`);
    if (!(isAfter(endDateTime, startDateTime) || isEqual(endDateTime, startDateTime))) {
        throw timeRangeNotValid;
    }
    if (!validateDateRangeMinMax(startDateTime, endDateTime, max, min)) {
        throw rangeIsNotBetweenAllowedMinMax;
    }
};
export const dateRangeValidator = (dateTimeString, max = MAX_DATE, min = MIN_DATE, joinString = 'to', dateFormat = 'MM/dd/yyyy') => {
    const dateRangeResult = getValidationAndExtractDateRange(dateTimeString, joinString, dateFormat);
    const timeRanges = extractTimeRange(dateTimeString, joinString);
    if (!dateRangeResult.isValid || timeRanges?.length) {
        throw dateRangeResult.error;
    }
    if (!(isAfter(new Date(`${dateRangeResult.endDate}`), new Date(`${dateRangeResult.startDate}`)) ||
        isEqual(new Date(`${dateRangeResult.endDate}`), new Date(`${dateRangeResult.startDate}`)))) {
        throw dateRangeNotValid;
    }
    if (!validateDateRangeMinMax(new Date(`${dateRangeResult.startDate}`), new Date(`${dateRangeResult.endDate}`), max, min)) {
        throw rangeIsNotBetweenAllowedMinMax;
    }
};
export const getFormatDateTime = (dateTimeObject, joinString) => {
    if (typeof dateTimeObject.selectedDate === 'string' &&
        dateTimeObject.selectedDate.length > 0 &&
        typeof dateTimeObject.endTime === 'string' &&
        dateTimeObject.endTime.length > 0 &&
        typeof dateTimeObject.startTime === 'string' &&
        dateTimeObject.startTime.length > 0) {
        return `${dateTimeObject.selectedDate} ( ${dateTimeObject.startTime} ${joinString} ${dateTimeObject.endTime})`;
    }
    if (typeof dateTimeObject.startDate === 'string' &&
        dateTimeObject.startDate.length > 0 &&
        typeof dateTimeObject.endDate === 'string' &&
        dateTimeObject.endDate.length > 0 &&
        typeof dateTimeObject.endTime === 'string' &&
        dateTimeObject.endTime.length > 0 &&
        typeof dateTimeObject.startTime === 'string' &&
        dateTimeObject.startTime.length > 0) {
        return `${dateTimeObject.startDate} ${joinString} ${dateTimeObject.endDate} (${dateTimeObject.startTime} ${joinString} ${dateTimeObject.endTime})`;
    }
    if (typeof dateTimeObject.selectedDate === 'string' && dateTimeObject.selectedDate.length > 0) {
        return `${dateTimeObject.selectedDate}`;
    }
    if (typeof dateTimeObject.startDate === 'string' &&
        dateTimeObject.startDate.length > 0 &&
        typeof dateTimeObject.endDate === 'string' &&
        dateTimeObject.endDate.length > 0) {
        return `${dateTimeObject.startDate} ${joinString} ${dateTimeObject.endDate}`;
    }
    return '';
};
export const layoutItemsModifier = (layoutItemsFunction) => {
    const listCreator = layoutItemsFunction(new Date());
    const filteredList = listCreator.filter(({ dateRange, hidden, value }) => !!dateRange &&
        !hidden &&
        ((!!dateRange.endDate &&
            !!dateRange.startDate &&
            !!isValid(dateRange.endDate) &&
            !!isValid(dateRange.startDate) &&
            (isAfter(dateRange.endDate, dateRange.startDate) || isEqual(dateRange.endDate, dateRange.startDate))) ||
            (!!dateRange.number && !!dateRange.type)) &&
        (value !== 'all' || value !== 'customs'));
    const foundCustom = listCreator.find(({ value }) => value === 'customs');
    const foundAll = listCreator.find(({ value }) => value === 'all');
    let listForRender = filteredList.reduce((prevStorage, current) => {
        const date = new Date();
        if (!!current?.dateRange && !!current.dateRange.number && !!current.dateRange.type) {
            let startDate;
            let endDate;
            if (current.dateRange.type === 'days') {
                if (current.dateRange.number < 0) {
                    startDate = addDays(date, current.dateRange.number);
                    endDate = date;
                }
                else {
                    startDate = date;
                    endDate = addDays(date, current.dateRange.number);
                }
            }
            else if (current.dateRange.type === 'hours') {
                if (current.dateRange.number < 0) {
                    startDate = addHours(date, current.dateRange.number);
                    endDate = date;
                }
                else {
                    startDate = date;
                    endDate = addHours(date, current.dateRange.number);
                }
            }
            else if (current.dateRange.type === 'months') {
                if (current.dateRange.number < 0) {
                    startDate = addMonths(date, current.dateRange.number);
                    endDate = date;
                }
                else {
                    startDate = date;
                    endDate = addMonths(date, current.dateRange.number);
                }
            }
            else {
                if (current.dateRange.number < 0) {
                    startDate = addYears(date, current.dateRange.number);
                    endDate = date;
                }
                else {
                    startDate = date;
                    endDate = addYears(date, current.dateRange.number);
                }
            }
            return [...prevStorage, { label: current.label, value: current.value, dateRange: { endDate, startDate } }];
        }
        return [...prevStorage, current];
    }, []);
    if (!foundAll) {
        listForRender = [...listForRender, { value: 'all', label: 'All' }];
    }
    else if (!foundAll['hidden']) {
        listForRender = [...listForRender, foundAll];
    }
    if (!foundCustom) {
        listForRender = [...listForRender, { value: 'customs', label: 'Customs' }];
    }
    else if (!foundCustom['hidden']) {
        listForRender = [...listForRender, foundCustom];
    }
    return listForRender;
};
export const durationsItemsModifier = (durationsItem) => {
    let filteredList = durationsItem.filter(({ hidden, value }) => !hidden && value !== 'customs');
    const foundCustom = durationsItem.find(({ value }) => value === 'customs');
    if (!foundCustom) {
        filteredList = [
            ...filteredList,
            {
                value: 'customs',
                children: 'Customs'
            }
        ];
    }
    else if (!foundCustom['hidden']) {
        filteredList = [...filteredList, foundCustom];
    }
    return filteredList;
};
export const dateRangeToStringRange = (start, end, dateFormat, timeFormat) => {
    const startDate = (start && format(start, dateFormat)) || '';
    const endDate = (end && format(end, dateFormat)) || '';
    const startTime = (start && format(start, timeFormat)) || '';
    const endTime = (end && format(end, timeFormat)) || '';
    return { startDate, endDate, startTime, endTime };
};
export const getAutoCompleteTime = ({ startTime, endTime, durationSelected, timeFormat, timeRange }) => {
    const { value, type, durations } = durationSelected;
    if (!value || value === 'customs') {
        return { startTime: startTime || timeRange.startTime || '', endTime: endTime || timeRange.endTime || '' };
    }
    const start = startTime ? parse(startTime, timeFormat, new Date()) : null;
    const end = endTime ? parse(endTime, timeFormat, new Date()) : null;
    let startDate;
    let endDate;
    if (type === 'hours') {
        if (start) {
            startDate = start;
            endDate = addHours(start, durations);
        }
        else if (end) {
            startDate = addHours(end, -1 * durations);
            endDate = end;
        }
    }
    else if (type === 'minutes') {
        if (start) {
            startDate = start;
            endDate = addMinutes(start, durations);
        }
        else if (end) {
            startDate = addMinutes(end, -1 * durations);
            endDate = end;
        }
    }
    return { startTime: format(startDate, timeFormat), endTime: format(endDate, timeFormat) };
};
export const getDisabledStateForDTRP = (initialDateRange, dateRange, timeRange, timeRangeError, dateFormat, timeFormat, timeCardOption) => {
    if (timeCardOption) {
        return (!dateRange?.start ||
            !dateRange?.end ||
            !!timeRangeError?.error ||
            !timeRange.startTime ||
            !timeRange.endTime ||
            (!!initialDateRange?.end &&
                format(dateRange.end, dateFormat) === format(initialDateRange.end, dateFormat) &&
                format(initialDateRange.end, timeFormat) === timeRange.endTime.trim() &&
                !!initialDateRange?.start &&
                format(dateRange.start, dateFormat) === format(initialDateRange.start, dateFormat) &&
                format(initialDateRange.start, timeFormat) === timeRange.startTime.trim()));
    }
    return (!dateRange?.start ||
        !dateRange?.end ||
        (!!initialDateRange?.end &&
            format(dateRange.end, dateFormat) === format(initialDateRange.end, dateFormat) &&
            !!initialDateRange?.start &&
            format(dateRange.start, dateFormat) === format(initialDateRange.start, dateFormat)));
};
//# sourceMappingURL=date2.logic.js.map